import { z } from 'zod';
import { AddressSchema, AllPaymentProviderSchema } from '../types/common-tools';
import { SourceStatusSchema } from '../types/status';
import { CheckoutCapabilityGrantSchema, OneTimeCheckoutInstructionSchema, OneTimeCheckoutPaymentSchema, ProviderPaymentPresentationSchema, } from '../public-experiences/checkout';
import { CursorQuerySchema, createCursorPageSchema, } from '../public-experiences/customers';
import { CheckoutSourceKindEnum } from '../public-experiences/enums';
import { ProviderExecutionScopeSchema } from '../public-experiences/execution-scope';
import { PointPayAccountContextSchema } from '../public-experiences/pointpay';
import { BusinessIdSchema, CheckoutIdSchema, HttpsUrlSchema, IsoTimestampSchema, PublishedRevisionSchema, Sha256HexSchema, StripeCheckoutSessionIdSchema, StripeCustomerIdSchema, StripePaymentIntentIdSchema, } from '../public-experiences/primitives';
import { SaleOrderDetailsSchema } from '../types/sale/order-details';
import { PointPaySaleIdSchema } from '../types/sale/sale-links';
import { ProcessingConfigSchema } from '../types/user/web';
/* ────────────────────────────────────────────────────────────────
   Payment Link Status Enum
   ──────────────────────────────────────────────────────────────── */
/**
 * Enum representing the status of a payment link.
 *
 * Possible values:
 * - ACTIVE: The payment link is active and can accept payments.
 * - PAID: The payment has been completed.
 * - EXPIRED: The payment link has expired.
 * - CANCELLED: The payment link has been cancelled.
 */
export var PaymentLinkStatusEnum;
(function (PaymentLinkStatusEnum) {
    PaymentLinkStatusEnum["ACTIVE"] = "ACTIVE";
    PaymentLinkStatusEnum["PAID"] = "PAID";
    PaymentLinkStatusEnum["EXPIRED"] = "EXPIRED";
    PaymentLinkStatusEnum["CANCELLED"] = "CANCELLED";
})(PaymentLinkStatusEnum || (PaymentLinkStatusEnum = {}));
/**
 * Zod schema for PaymentLinkStatusEnum.
 */
export const PaymentLinkStatusSchema = z.nativeEnum(PaymentLinkStatusEnum);
/* ────────────────────────────────────────────────────────────────
   Nested Schemas
   ──────────────────────────────────────────────────────────────── */
/**
 * Schema for business information displayed on payment link.
 */
export const PaymentLinkBusinessSchema = z.object({
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    address: AddressSchema.nullable(),
});
/**
 * Schema for customer information on payment link.
 */
export const PaymentLinkCustomerSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
});
/**
 * Schema for sale details on payment link.
 */
export const PaymentLinkSaleDetailsSchema = z.object({
    date: z.date(),
    customerNotes: z.string().nullable(),
    terms: z.string().nullable(),
});
/**
 * Schema for individual line items on payment link.
 */
export const PaymentLinkItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    price: z.number(),
    quantity: z.number(),
    total: z.number(),
});
/**
 * Schema for financial summary on payment link.
 */
export const PaymentLinkFinancialSchema = z.object({
    subTotal: z.number(),
    taxes: z.number(),
    discount: z.number(),
    processingFees: z.number(),
    shippingCharges: z.number(),
    total: z.number(),
    balance: z.number(),
});
/* ────────────────────────────────────────────────────────────────
   Main Payment Link Document Schema
   ──────────────────────────────────────────────────────────────── */
/**
 * Schema for the complete payment link document.
 *
 * Contains all information needed to render and process a payment link:
 * - Payment intent data (Tilled integration)
 * - Sale identification
 * - Business information
 * - Customer information (optional)
 * - Sale details
 * - Line items
 * - Financial summary
 * - Link metadata
 */
export const PaymentLinkDocumentSchema = z.object({
    // only TILLED and STRIPE are supported for payment links
    provider: AllPaymentProviderSchema,
    // Payment Intent Data
    paymentIntentId: z.string().nullable(),
    clientSecret: z.string(),
    amountCents: z.number(),
    currency: z.string(),
    paymentStatus: SourceStatusSchema,
    accountId: z.string().nullable(),
    // Sale Identification
    saleId: z.string(),
    shortIdentifier: z.string(),
    businessId: z.string(),
    businessLocationId: z.string(),
    // Business Info
    business: PaymentLinkBusinessSchema,
    // Customer Info (optional)
    customer: PaymentLinkCustomerSchema.nullable(),
    // Sale Details
    saleDetails: PaymentLinkSaleDetailsSchema,
    // Line Items
    items: PaymentLinkItemSchema.array(),
    // Financial Summary
    financial: PaymentLinkFinancialSchema,
    // Metadata
    linkStatus: PaymentLinkStatusSchema,
    createdAt: z.date(),
    updatedAt: z.date(),
    expiresAt: z.date().nullable(),
});
/* New requests are server-only. Keep the legacy contract above for sent links
 * and Donations until their real transition is established. */
export const PaymentLinkRequestIdSchema = z.string().uuid();
/** 32 random bytes, base64url; retained privately for authorized copy/resend. */
export const PaymentLinkInvitationSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]{43}$/);
export const PaymentLinkRequestStatusSchema = z.enum([
    'ACTIVE',
    'PROCESSING',
    'PAID',
    'EXPIRED',
    'CANCELLED',
    'STALE',
]);
export const PaymentLinkAttemptStatusSchema = z.enum([
    'INITIALIZING',
    'PENDING_PAYMENT',
    'PROCESSING',
    'CONFIRMED',
    'EXPIRED',
    'FAILED',
    'UNCERTAIN',
]);
/** Provider success precedes durable SQL linkage; neither depends on invoices. */
export const PaymentLinkFinancialStateSchema = z.enum([
    'UNCOLLECTED',
    'VERIFIED',
    'LINKED',
]);
/** Account-directory reference, never a claim about the actual payer. */
export const PaymentLinkIntendedRecipientSchema = z
    .object({
    customerId: StripeCustomerIdSchema,
    accountContext: PointPayAccountContextSchema,
})
    .strict();
export const PaymentLinkCreateRequestSchema = OneTimeCheckoutPaymentSchema.extend({
    idempotencyKey: z.string().uuid(),
    intendedRecipient: PaymentLinkIntendedRecipientSchema.optional(),
    expiresAt: IsoTimestampSchema.optional(),
    sourceSaleId: PointPaySaleIdSchema.optional(),
    orderDetails: SaleOrderDetailsSchema.nullable().optional(),
}).strict();
export const PaymentLinkListQuerySchema = CursorQuerySchema.extend({
    status: PaymentLinkRequestStatusSchema.optional(),
}).strict();
export const PaymentLinkRequestParamsSchema = z
    .object({ id: PaymentLinkRequestIdSchema })
    .strict();
export const PaymentLinkPublicParamsSchema = z
    .object({ invitation: PaymentLinkInvitationSchema })
    .strict();
export const PaymentLinkCancelRequestSchema = z
    .object({ revision: PublishedRevisionSchema })
    .strict();
export const PaymentLinkSendRequestSchema = z.discriminatedUnion('channel', [
    z
        .object({
        channel: z.literal('EMAIL'),
        destination: z.string().trim().email().max(320),
        revision: PublishedRevisionSchema,
    })
        .strict(),
    z
        .object({
        channel: z.literal('SMS'),
        destination: z.string().regex(/^\+[1-9]\d{7,14}$/),
        revision: PublishedRevisionSchema,
    })
        .strict(),
]);
/** Latest transport attempt; contains no destination or message content. */
export const PaymentLinkDeliverySchema = z
    .object({
    status: z.enum(['ACCEPTED', 'FAILED']),
    channel: z.enum(['EMAIL', 'SMS']),
    attemptedAt: IsoTimestampSchema,
})
    .strict();
export const PaymentLinkMerchantSummarySchema = OneTimeCheckoutPaymentSchema.extend({
    requestId: PaymentLinkRequestIdSchema,
    revision: PublishedRevisionSchema,
    status: PaymentLinkRequestStatusSchema,
    saleId: PointPaySaleIdSchema,
    intendedRecipient: PaymentLinkIntendedRecipientSchema.nullable(),
    /** Verified provider PaymentIntent for existing PointPay payment navigation. */
    paymentId: StripePaymentIntentIdSchema.nullable(),
    expiresAt: IsoTimestampSchema.nullable(),
    lastSentAt: IsoTimestampSchema.nullable(),
    lastDelivery: PaymentLinkDeliverySchema.nullable(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
}).strict();
export const PaymentLinkMerchantDetailSchema = PaymentLinkMerchantSummarySchema.extend({
    /** Only authenticated detail/create includes the invitation URL. */
    paymentUrl: HttpsUrlSchema,
    /** Retained payment navigation uses the request's frozen account scope. */
    accountContext: PointPayAccountContextSchema,
}).strict();
export const PaymentLinkListResponseSchema = z
    .object({
    page: createCursorPageSchema(PaymentLinkMerchantSummarySchema),
})
    .strict();
export const PaymentLinkSendResponseSchema = z
    .object({
    requestId: PaymentLinkRequestIdSchema,
    /** Acceptance is not evidence of delivery or opening. */
    status: z.literal('ACCEPTED'),
    acceptedAt: IsoTimestampSchema,
})
    .strict();
/** Invitation-only projection: no recipient, payer, provider or sale identity. */
export const PaymentLinkPublicSummarySchema = OneTimeCheckoutPaymentSchema.extend({
    merchant: z.object({ name: z.string().min(1).max(200) }).strict(),
    status: PaymentLinkRequestStatusSchema,
    expiresAt: IsoTimestampSchema.nullable(),
}).strict();
/** Client ids identify a resume only; the server claims new attempt identities. */
export const PaymentLinkCheckoutRequestSchema = z.discriminatedUnion('action', [
    z
        .object({
        action: z.literal('START'),
        recaptchaToken: z.string().min(1).max(4096),
    })
        .strict(),
    z
        .object({
        action: z.literal('RESUME'),
        checkoutId: CheckoutIdSchema,
        recaptchaToken: z.string().min(1).max(4096),
    })
        .strict(),
    z
        .object({
        action: z.literal('TAKEOVER'),
        recaptchaToken: z.string().min(1).max(4096),
    })
        .strict(),
]);
export const PaymentLinkCheckoutResponseSchema = z
    .object({
    checkoutId: CheckoutIdSchema,
    status: PaymentLinkAttemptStatusSchema,
    grant: CheckoutCapabilityGrantSchema.nullable(),
    payment: ProviderPaymentPresentationSchema.nullable(),
})
    .strict()
    .superRefine((response, ctx) => {
    if (response.payment !== null &&
        (response.grant === null || response.status !== 'PENDING_PAYMENT'))
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['payment'],
            message: 'Payment presentation requires a capability and payable attempt',
        });
});
/** Read with X-Checkout-Capability, not the invitation alone. */
export const PaymentLinkCheckoutStatusResponseSchema = z
    .object({
    checkoutId: CheckoutIdSchema,
    status: PaymentLinkAttemptStatusSchema,
    requestStatus: PaymentLinkRequestStatusSchema,
})
    .strict();
/** Existing rule bounds/flatFee stay in dollars. The computed fee is cents.
 * Missing rate cannot silently pick ProcessingConfig's legacy default. */
export const PaymentLinkFeeSnapshotSchema = z
    .object({
    source: z.literal('onlineProcessingConfig'),
    config: ProcessingConfigSchema.extend({
        rate: z.number().finite().min(0).max(1),
    }).strict(),
    applicationFeeAmountMinor: z.number().int().nonnegative().safe(),
})
    .strict()
    .superRefine((snapshot, ctx) => {
    snapshot.config.flatFeeRules.forEach((rule, index) => {
        if (!Number.isFinite(rule.min) ||
            !Number.isFinite(rule.max) ||
            !Number.isFinite(rule.flatFee) ||
            rule.min < 0 ||
            rule.max < rule.min ||
            rule.flatFee < 0)
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['config', 'flatFeeRules', index],
                message: 'Fee rules require finite nonnegative values and ordered bounds',
            });
    });
    if (snapshot.config.customerSplits?.rate !== 0 ||
        snapshot.config.customerSplits?.fixed !== 0)
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['config', 'customerSplits'],
            message: 'One-time requests preserve merchant-paid fees with zero customer splits',
        });
});
/** Server-only request. SQL Sales remains the obligation authority. */
export const PaymentLinkRequestDocumentSchema = OneTimeCheckoutPaymentSchema.extend({
    schemaVersion: z.literal(1),
    sourceKind: z.literal(CheckoutSourceKindEnum.PAYMENT_LINK),
    requestId: PaymentLinkRequestIdSchema,
    businessId: BusinessIdSchema,
    businessLocationId: z.string().min(1).max(128),
    revision: PublishedRevisionSchema,
    requestHash: Sha256HexSchema,
    idempotencyKey: z.string().uuid(),
    invitation: PaymentLinkInvitationSchema,
    invitationHash: Sha256HexSchema,
    saleId: PointPaySaleIdSchema,
    intendedRecipient: PaymentLinkIntendedRecipientSchema.nullable(),
    executionScope: ProviderExecutionScopeSchema.refine((scope) => scope.chargeModel === 'DIRECT', 'New payment requests require a DIRECT account scope'),
    status: PaymentLinkRequestStatusSchema,
    activeCheckoutId: CheckoutIdSchema.nullable(),
    /** Never cleared by refunds, disputes, expiry or merchant cancellation. */
    collectedAt: IsoTimestampSchema.nullable(),
    paymentIntentId: StripePaymentIntentIdSchema.nullable(),
    expiresAt: IsoTimestampSchema.nullable(),
    lastSentAt: IsoTimestampSchema.nullable(),
    lastDelivery: PaymentLinkDeliverySchema.nullable(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
}).strict();
/** Private attempt inside the existing pxCheckouts collection-group boundary. */
export const PaymentLinkAttemptDocumentSchema = z
    .object({
    schemaVersion: z.literal(1),
    sourceKind: z.literal(CheckoutSourceKindEnum.PAYMENT_LINK),
    checkoutId: CheckoutIdSchema,
    businessId: BusinessIdSchema,
    requestId: PaymentLinkRequestIdSchema,
    requestRevision: PublishedRevisionSchema,
    status: PaymentLinkAttemptStatusSchema,
    financialState: PaymentLinkFinancialStateSchema,
    instruction: OneTimeCheckoutInstructionSchema,
    feeSnapshot: PaymentLinkFeeSnapshotSchema,
    requestHash: Sha256HexSchema,
    capabilityHash: Sha256HexSchema,
    capabilityExpiresAt: IsoTimestampSchema,
    /** Provider identities are absent until preparation/reconciliation finds them. */
    sessionId: StripeCheckoutSessionIdSchema.optional(),
    /** Checkpoint for bounded read-only recovery when a create response was lost. */
    sessionRecoveryCursor: StripeCheckoutSessionIdSchema.max(255).optional(),
    paymentIntentId: StripePaymentIntentIdSchema.optional(),
    payerCustomerId: StripeCustomerIdSchema.optional(),
    confirmedAt: IsoTimestampSchema.optional(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
})
    .strict()
    .superRefine((attempt, ctx) => {
    const { source, merchant } = attempt.instruction;
    if (source.checkoutId !== attempt.checkoutId ||
        source.requestId !== attempt.requestId ||
        source.businessId !== attempt.businessId ||
        source.requestRevision !== attempt.requestRevision ||
        merchant.applicationFeeAmountMinor !==
            attempt.feeSnapshot.applicationFeeAmountMinor)
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['instruction'],
            message: 'Attempt must preserve the frozen source, revision and online fee',
        });
    if (attempt.financialState !== 'UNCOLLECTED' &&
        (!attempt.paymentIntentId || !attempt.sessionId || !attempt.confirmedAt))
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['financialState'],
            message: 'Collection requires verified Session and PaymentIntent identities',
        });
});
/** Same fenced operation fields as the existing checkout recovery algorithm. */
export const PaymentLinkSessionOperationDocumentSchema = z
    .object({
    schemaVersion: z.literal(1),
    kind: z.literal('SESSION'),
    checkoutId: CheckoutIdSchema,
    requestHash: Sha256HexSchema,
    requestSnapshot: z
        .object({ instruction: OneTimeCheckoutInstructionSchema })
        .strict(),
    state: z.enum(['NOT_STARTED', 'IN_FLIGHT', 'SUCCEEDED', 'UNCERTAIN']),
    firstDispatchedAt: IsoTimestampSchema.nullable(),
    retrySafeUntil: IsoTimestampSchema.nullable(),
    leaseOwner: z.string().min(1).max(128).nullable(),
    leaseExpiresAt: IsoTimestampSchema.nullable(),
    providerObjectId: StripeCheckoutSessionIdSchema.nullable(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
})
    .strict()
    .superRefine((operation, ctx) => {
    if (operation.checkoutId !==
        operation.requestSnapshot.instruction.source.checkoutId)
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['checkoutId'],
            message: 'Operation must identify the original checkout',
        });
    if ((operation.leaseOwner === null) !== (operation.leaseExpiresAt === null) ||
        (operation.state === 'IN_FLIGHT') !== (operation.leaseOwner !== null))
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['leaseOwner'],
            message: 'Only in-flight operations carry complete leases',
        });
    if ((operation.firstDispatchedAt === null) !==
        (operation.retrySafeUntil === null) ||
        (operation.state === 'NOT_STARTED'
            ? operation.firstDispatchedAt !== null
            : operation.firstDispatchedAt === null) ||
        (operation.firstDispatchedAt &&
            operation.retrySafeUntil &&
            Date.parse(operation.retrySafeUntil) <=
                Date.parse(operation.firstDispatchedAt)))
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['retrySafeUntil'],
            message: 'Dispatched operations require a bounded retry horizon',
        });
    if ((operation.state === 'SUCCEEDED' && !operation.providerObjectId) ||
        (operation.state === 'NOT_STARTED' && operation.providerObjectId))
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['providerObjectId'],
            message: 'Provider identity must match operation progress',
        });
});
